/**
 * Trade Analyzer Modal
 *
 * Displays comprehensive trade analytics from the trade-analyzer service.
 * Sections: overview stats, MFE/MAE optimization, what-if scenarios,
 * breakdowns by session/symbol/day/hour.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TradeAnalyzerModal.css';

// ---------- Types ----------

interface BreakdownEntry {
  trades: number;
  pnl: number;
  winRate: number;
}

interface DistBucket {
  bucket: string;
  count: number;
  pctOfTrades: number;
}

interface ExitEffBucket {
  bucket: string;
  count: number;
  avgPnl: number;
}

interface WhatIfScenario {
  tp: number;
  sl: number;
  estimatedWinRate: number;
  estimatedPF: number;
  estimatedAvgTrade: number;
}

interface MFEMAEAnalysis {
  avgMFE: number;
  avgMAE: number;
  avgExitEfficiency: number;
  avgMFEToMAERatio: number;
  optimalTPPoints: number;
  currentAvgTPPoints: number;
  tpCaptureRate: number;
  optimalSLPoints: number;
  currentAvgSLPoints: number;
  slHitRate: number;
  mfeDistribution: DistBucket[];
  maeDistribution: DistBucket[];
  exitEfficiencyDistribution: ExitEffBucket[];
  whatIfScenarios: WhatIfScenario[];
}

interface DailyStats {
  date: string;
  totalPnl: number;
  netPnl: number;
  tradeCount: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  largestWin: number;
  largestLoss: number;
}

interface AnalyticsReport {
  totalTrades: number;
  totalPnl: number;
  totalNetPnl: number;
  winRate: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  avgRR: number;
  largestWin: number;
  largestLoss: number;
  maxConsecutiveWins: number;
  maxConsecutiveLosses: number;
  maxDrawdown: number;
  mfeMAE: MFEMAEAnalysis;
  dailyStats: DailyStats[];
  bySession: Record<string, BreakdownEntry>;
  bySymbol: Record<string, BreakdownEntry>;
  byDayOfWeek: Record<string, BreakdownEntry>;
  byHour: Record<string, BreakdownEntry>;
  currentStreak: { type: 'win' | 'loss'; count: number };
}

// ---------- Constants ----------

// Use the main backend URL + proxy route (trade-analyzer service is not directly exposed)
import { getBackendUrl } from '../config/ports';
const TRADE_ANALYZER_URL = getBackendUrl();

type TabType = 'overview' | 'mfe-mae' | 'what-if' | 'breakdowns' | 'daily';

// ---------- Helpers ----------

function fmtCurrency(v: number): string {
  const sign = v >= 0 ? '' : '-';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

function fmtNum(v: number, decimals = 2): string {
  return v.toFixed(decimals);
}

function pnlClass(v: number): string {
  if (v > 0) return 'positive';
  if (v < 0) return 'negative';
  return '';
}

// ---------- Props ----------

interface TradeAnalyzerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ---------- Component ----------

const TradeAnalyzerModal: React.FC<TradeAnalyzerModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [report, setReport] = useState<AnalyticsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchReport = useCallback(async (start?: string, end?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (start) params.set('startDate', start);
      if (end) params.set('endDate', end);
      const qs = params.toString();
      const url = `${TRADE_ANALYZER_URL}/api/trade-analyzer/report${qs ? `?${qs}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: AnalyticsReport = await res.json();
      setReport(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load report: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open
  useEffect(() => {
    if (isOpen) {
      fetchReport(startDate || undefined, endDate || undefined);
    }
  }, [isOpen, fetchReport, startDate, endDate]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleApplyFilter = () => {
    fetchReport(startDate || undefined, endDate || undefined);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchReport();
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'mfe-mae', label: 'MFE / MAE' },
    { key: 'what-if', label: 'What-If' },
    { key: 'breakdowns', label: 'Breakdowns' },
    { key: 'daily', label: 'Daily' },
  ];

  const modal = (
    <div className="ta-backdrop" onClick={onClose}>
      <div className="ta-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ta-header">
          <div className="ta-header-left">
            <h2 className="ta-title">Trade Analytics</h2>
            {report && !loading && (
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                {report.totalTrades} trades
              </span>
            )}
          </div>
          <button className="ta-close" onClick={onClose}>&times;</button>
        </div>

        {/* Date Filter */}
        <div className="ta-filters">
          <span className="ta-filter-label">From</span>
          <input
            type="date"
            className="ta-filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span className="ta-filter-label">To</span>
          <input
            type="date"
            className="ta-filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <button className="ta-filter-btn" onClick={handleApplyFilter}>Apply</button>
          {(startDate || endDate) && (
            <button className="ta-filter-btn" onClick={handleClearFilter}>Clear</button>
          )}
        </div>

        {/* Tabs */}
        <div className="ta-tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`ta-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="ta-content">
          {loading ? (
            <div className="ta-loading">Loading analytics...</div>
          ) : error ? (
            <div className="ta-error">{error}</div>
          ) : report ? (
            <>
              {activeTab === 'overview' && <OverviewTab report={report} />}
              {activeTab === 'mfe-mae' && <MFEMAETab mfe={report.mfeMAE} />}
              {activeTab === 'what-if' && <WhatIfTab scenarios={report.mfeMAE.whatIfScenarios} />}
              {activeTab === 'breakdowns' && <BreakdownsTab report={report} />}
              {activeTab === 'daily' && <DailyTab dailyStats={report.dailyStats} />}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

// ---------- Overview Tab ----------

const OverviewTab: React.FC<{ report: AnalyticsReport }> = ({ report }) => {
  const streak = report.currentStreak;
  return (
    <>
      <div className="ta-stats-grid">
        <div className="ta-stat-card">
          <div className="ta-stat-value neutral">{report.totalTrades}</div>
          <div className="ta-stat-label">Total Trades</div>
        </div>
        <div className="ta-stat-card">
          <div className={`ta-stat-value ${report.winRate >= 50 ? 'positive' : 'negative'}`}>
            {fmtPct(report.winRate)}
          </div>
          <div className="ta-stat-label">Win Rate</div>
        </div>
        <div className="ta-stat-card">
          <div className={`ta-stat-value ${report.profitFactor >= 1 ? 'positive' : 'negative'}`}>
            {fmtNum(report.profitFactor)}
          </div>
          <div className="ta-stat-label">Profit Factor</div>
        </div>
        <div className="ta-stat-card">
          <div className={`ta-stat-value ${pnlClass(report.totalNetPnl)}`}>
            {fmtCurrency(report.totalNetPnl)}
          </div>
          <div className="ta-stat-label">Net P&L</div>
        </div>
        <div className="ta-stat-card">
          <div className="ta-stat-value negative">{fmtCurrency(report.maxDrawdown)}</div>
          <div className="ta-stat-label">Max Drawdown</div>
        </div>
        <div className="ta-stat-card">
          <div className={`ta-stat-value ${streak.type === 'win' ? 'positive' : 'negative'}`}>
            <span className={`ta-streak ${streak.type}`}>
              {streak.count} {streak.type === 'win' ? 'W' : 'L'}
            </span>
          </div>
          <div className="ta-stat-label">Current Streak</div>
        </div>
      </div>

      {/* Win/Loss Stats */}
      <div className="ta-section">
        <div className="ta-section-title">Win / Loss Details</div>
        <div className="ta-stats-grid">
          <div className="ta-stat-card">
            <div className="ta-stat-value positive">{fmtCurrency(report.avgWin)}</div>
            <div className="ta-stat-label">Avg Win</div>
          </div>
          <div className="ta-stat-card">
            <div className="ta-stat-value negative">{fmtCurrency(report.avgLoss)}</div>
            <div className="ta-stat-label">Avg Loss</div>
          </div>
          <div className="ta-stat-card">
            <div className="ta-stat-value positive">{fmtCurrency(report.largestWin)}</div>
            <div className="ta-stat-label">Largest Win</div>
          </div>
          <div className="ta-stat-card">
            <div className="ta-stat-value negative">{fmtCurrency(report.largestLoss)}</div>
            <div className="ta-stat-label">Largest Loss</div>
          </div>
          <div className="ta-stat-card">
            <div className="ta-stat-value neutral">{fmtNum(report.avgRR)}</div>
            <div className="ta-stat-label">Avg R:R</div>
          </div>
          <div className="ta-stat-card">
            <div className="ta-stat-value positive">{report.maxConsecutiveWins}</div>
            <div className="ta-stat-label">Max Consec. Wins</div>
          </div>
          <div className="ta-stat-card">
            <div className="ta-stat-value negative">{report.maxConsecutiveLosses}</div>
            <div className="ta-stat-label">Max Consec. Losses</div>
          </div>
        </div>
      </div>
    </>
  );
};

// ---------- MFE/MAE Tab ----------

const MFEMAETab: React.FC<{ mfe: MFEMAEAnalysis }> = ({ mfe }) => {
  return (
    <>
      {/* Highlight cards */}
      <div className="ta-mfe-highlights">
        <div className="ta-mfe-card">
          <div className="ta-mfe-card-label">Optimal TP</div>
          <div className="ta-mfe-card-value green">{fmtNum(mfe.optimalTPPoints)} pts</div>
          <div className="ta-mfe-card-sub">Current avg: {fmtNum(mfe.currentAvgTPPoints)} pts</div>
        </div>
        <div className="ta-mfe-card">
          <div className="ta-mfe-card-label">Optimal SL</div>
          <div className="ta-mfe-card-value red">{fmtNum(mfe.optimalSLPoints)} pts</div>
          <div className="ta-mfe-card-sub">Current avg: {fmtNum(mfe.currentAvgSLPoints)} pts</div>
        </div>
        <div className="ta-mfe-card">
          <div className="ta-mfe-card-label">Avg MFE</div>
          <div className="ta-mfe-card-value green">{fmtCurrency(mfe.avgMFE)}</div>
          <div className="ta-mfe-card-sub">TP capture rate: {fmtPct(mfe.tpCaptureRate)}</div>
        </div>
        <div className="ta-mfe-card">
          <div className="ta-mfe-card-label">Avg MAE</div>
          <div className="ta-mfe-card-value red">{fmtCurrency(mfe.avgMAE)}</div>
          <div className="ta-mfe-card-sub">SL hit rate: {fmtPct(mfe.slHitRate)}</div>
        </div>
        <div className="ta-mfe-card">
          <div className="ta-mfe-card-label">Exit Efficiency</div>
          <div className="ta-mfe-card-value">{fmtPct(mfe.avgExitEfficiency)}</div>
          <div className="ta-mfe-card-sub">MFE:MAE ratio: {fmtNum(mfe.avgMFEToMAERatio)}</div>
        </div>
      </div>

      {/* MFE Distribution */}
      <div className="ta-section">
        <div className="ta-section-title">MFE Distribution (Max Favorable Excursion)</div>
        <DistributionTable buckets={mfe.mfeDistribution} color="green" />
      </div>

      {/* MAE Distribution */}
      <div className="ta-section">
        <div className="ta-section-title">MAE Distribution (Max Adverse Excursion)</div>
        <DistributionTable buckets={mfe.maeDistribution} color="red" />
      </div>

      {/* Exit Efficiency Distribution */}
      <div className="ta-section">
        <div className="ta-section-title">Exit Efficiency Distribution</div>
        <div className="ta-table-wrap">
          <table className="ta-table">
            <thead>
              <tr>
                <th>Bucket</th>
                <th className="right">Trades</th>
                <th className="right">Avg P&L</th>
                <th style={{ width: '30%' }}>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {mfe.exitEfficiencyDistribution.map((b) => {
                const maxCount = Math.max(...mfe.exitEfficiencyDistribution.map(x => x.count), 1);
                const pct = (b.count / maxCount) * 100;
                return (
                  <tr key={b.bucket}>
                    <td>{b.bucket}</td>
                    <td className="right">{b.count}</td>
                    <td className="right" style={{ color: b.avgPnl >= 0 ? '#22c55e' : '#ef4444' }}>
                      {fmtCurrency(b.avgPnl)}
                    </td>
                    <td>
                      <div className="ta-dist-bar-bg">
                        <div
                          className="ta-dist-bar indigo"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ---------- Distribution Table ----------

const DistributionTable: React.FC<{
  buckets: DistBucket[];
  color: 'green' | 'red';
}> = ({ buckets, color }) => {
  const maxPct = Math.max(...buckets.map(b => b.pctOfTrades), 1);
  return (
    <div className="ta-table-wrap">
      <table className="ta-table">
        <thead>
          <tr>
            <th>Bucket</th>
            <th className="right">Trades</th>
            <th className="right">% of Total</th>
            <th style={{ width: '35%' }}>Distribution</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.bucket}>
              <td>{b.bucket}</td>
              <td className="right">{b.count}</td>
              <td className="right">{fmtPct(b.pctOfTrades)}</td>
              <td>
                <div className="ta-dist-bar-bg">
                  <div
                    className={`ta-dist-bar ${color}`}
                    style={{ width: `${(b.pctOfTrades / maxPct) * 100}%` }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------- What-If Tab ----------

const WhatIfTab: React.FC<{ scenarios: WhatIfScenario[] }> = ({ scenarios }) => {
  return (
    <div className="ta-section">
      <div className="ta-section-title">What-If TP/SL Scenarios</div>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 12, marginTop: 0 }}>
        Estimates if you had used different take-profit and stop-loss levels.
      </p>
      <div className="ta-table-wrap">
        <table className="ta-table">
          <thead>
            <tr>
              <th className="right">TP (pts)</th>
              <th className="right">SL (pts)</th>
              <th className="right">Est. Win Rate</th>
              <th className="right">Est. PF</th>
              <th className="right">Est. Avg Trade</th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => (
              <tr key={i}>
                <td className="right">{fmtNum(s.tp)}</td>
                <td className="right">{fmtNum(s.sl)}</td>
                <td className="right" style={{ color: s.estimatedWinRate >= 50 ? '#22c55e' : '#ef4444' }}>
                  {fmtPct(s.estimatedWinRate)}
                </td>
                <td className="right" style={{ color: s.estimatedPF >= 1 ? '#22c55e' : '#ef4444' }}>
                  {fmtNum(s.estimatedPF)}
                </td>
                <td className="right" style={{ color: s.estimatedAvgTrade >= 0 ? '#22c55e' : '#ef4444' }}>
                  {fmtCurrency(s.estimatedAvgTrade)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Breakdowns Tab ----------

const BreakdownsTab: React.FC<{ report: AnalyticsReport }> = ({ report }) => {
  return (
    <div className="ta-breakdown-grid">
      <BreakdownSection title="By Session" data={report.bySession} />
      <BreakdownSection title="By Symbol" data={report.bySymbol} />
      <BreakdownSection title="By Day of Week" data={report.byDayOfWeek} />
      <BreakdownSection title="By Hour (PST)" data={report.byHour} />
    </div>
  );
};

const BreakdownSection: React.FC<{
  title: string;
  data: Record<string, BreakdownEntry>;
}> = ({ title, data }) => {
  const entries = Object.entries(data).sort((a, b) => b[1].trades - a[1].trades);
  if (entries.length === 0) {
    return (
      <div className="ta-section">
        <div className="ta-section-title">{title}</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>No data</div>
      </div>
    );
  }
  return (
    <div className="ta-section">
      <div className="ta-section-title">{title}</div>
      <div className="ta-table-wrap">
        <table className="ta-table">
          <thead>
            <tr>
              <th>{title.replace('By ', '')}</th>
              <th className="right">Trades</th>
              <th className="right">P&L</th>
              <th className="right">Win Rate</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, val]) => (
              <tr key={key}>
                <td>{key}</td>
                <td className="right">{val.trades}</td>
                <td className="right" style={{ color: val.pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                  {fmtCurrency(val.pnl)}
                </td>
                <td className="right" style={{ color: val.winRate >= 50 ? '#22c55e' : '#ef4444' }}>
                  {fmtPct(val.winRate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Daily Tab ----------

const DailyTab: React.FC<{ dailyStats: DailyStats[] }> = ({ dailyStats }) => {
  if (dailyStats.length === 0) {
    return <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>No daily data available.</div>;
  }

  // Show newest first
  const sorted = [...dailyStats].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="ta-section">
      <div className="ta-section-title">Daily Performance</div>
      <div className="ta-table-wrap">
        <table className="ta-table">
          <thead>
            <tr>
              <th>Date</th>
              <th className="right">Trades</th>
              <th className="right">W/L</th>
              <th className="right">Win Rate</th>
              <th className="right">Net P&L</th>
              <th className="right">PF</th>
              <th className="right">Largest Win</th>
              <th className="right">Largest Loss</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr key={d.date}>
                <td>{d.date}</td>
                <td className="right">{d.tradeCount}</td>
                <td className="right">
                  <span style={{ color: '#22c55e' }}>{d.winCount}</span>
                  {' / '}
                  <span style={{ color: '#ef4444' }}>{d.lossCount}</span>
                </td>
                <td className="right" style={{ color: d.winRate >= 50 ? '#22c55e' : '#ef4444' }}>
                  {fmtPct(d.winRate)}
                </td>
                <td className="right" style={{ color: d.netPnl >= 0 ? '#22c55e' : '#ef4444' }}>
                  {fmtCurrency(d.netPnl)}
                </td>
                <td className="right" style={{ color: (d.profitFactor ?? 0) >= 1 ? '#22c55e' : '#ef4444' }}>
                  {d.profitFactor == null ? '∞' : fmtNum(d.profitFactor)}
                </td>
                <td className="right" style={{ color: '#22c55e' }}>{fmtCurrency(d.largestWin)}</td>
                <td className="right" style={{ color: '#ef4444' }}>{fmtCurrency(d.largestLoss)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TradeAnalyzerModal;
